import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useModelStore } from '@editorStores'
import useElementTypes from './useElementTypes'
import useResultsSelection from './useResultsSelection'

/**
 * Returns a callback that selects a model element in Results2 and navigates there
 * from any editor view (global loads, structural planning, …). The element's type
 * determines the matching Results2 tab — this mirrors the scene click handling in
 * Results2Scene's `onClickElement`. Returns whether the guid mapped to a
 * selectable Results2 position.
 */
const useJumpToResultElement = (): ((guid: string) => boolean) => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const elementTypes = useElementTypes()
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const { selectResultsPosition: selectPosition, selectResultsSurface: selectSurface } =
    useResultsSelection()

  return useCallback(
    (guid: string): boolean => {
      const elementType = elementTypes[guid]
      if (!elementType) return false

      switch (elementType) {
        case 'rips':
          selectPosition(guid, 'walls', 'wall-rips')
          break
        case 'lintels':
          selectPosition(guid, 'walls', 'wall-lintels')
          break
        case 'slab_beams':
          selectPosition(guid, 'slab-beams')
          break
        case 'roof_slab_beams':
          selectPosition(guid, 'roof-slab-beams')
          break
        case 'beams':
        case 'columns':
        case 'purlins':
          selectPosition(guid, elementType)
          break
        case 'inner_walls':
        case 'outer_walls':
          selectPosition(guid, 'walls', 'wall-assemblies')
          break
        case 'vertical_slabs': {
          // A Decke with a spanning beam opens the merged surface tab; a bare slab
          // opens the Decken tab.
          const hasBeam = !!verticalSlabs.find(slab => slab.guid === guid)?.beam
          if (hasBeam) selectSurface(guid, 'slab-beams')
          else selectPosition(guid, 'slabs')
          break
        }
        case 'vertical_roof_slabs': {
          const hasBeam = !!verticalRoofSlabs.find(slab => slab.guid === guid)?.beam
          if (hasBeam) selectSurface(guid, 'roof-slab-beams')
          else selectPosition(guid, 'roof-slabs')
          break
        }
        case 'slabs':
          selectPosition(guid, 'slabs')
          break
        case 'roof_slabs':
          selectPosition(guid, 'roof-slabs')
          break
        default:
          // standard_rips / foundation and anything else aren't selectable positions.
          return false
      }

      navigate(`/editor/${projectId}/results`)
      return true
    },
    [
      elementTypes,
      verticalSlabs,
      verticalRoofSlabs,
      selectPosition,
      selectSurface,
      navigate,
      projectId,
    ],
  )
}

export default useJumpToResultElement
